'use client'
import { TimeDisplay } from '@/components/ui/time-display'
import { Alert } from '@/lib/store/core/alerts'
import { cn } from '@/lib/utils'
import { Button, Chip } from '@heroui/react'
import Link from 'next/link'
import { useState } from 'react'
import { Icon } from '../ui/icon'

// Tone lookup for the standalone (root-mounted, isStandalone) critical-alert
// card. Every class here is a brand semantic token defined in BOTH theme
// scope (choice-graphics.css), so this alert
// renders correctly whether the current route is still v1 or has moved to
// v2 -- no per-route data-theme branching needed inside the card itself
// (that lives on the fixed-position wrapper in alert-portal.tsx instead).
const ALERT_TONE: Record<'primary' | 'success' | 'warning' | 'danger' | 'default', {
  bg: string
  border: string
  icon: string
}> = {
  primary: { bg: 'bg-info/10', border: 'border-info/30', icon: 'text-info' },
  success: { bg: 'bg-success/10', border: 'border-success/30', icon: 'text-success' },
  warning: { bg: 'bg-warning/10', border: 'border-warning/30', icon: 'text-warning' },
  danger: { bg: 'bg-danger/10', border: 'border-danger/30', icon: 'text-danger' },
  default: { bg: 'bg-default/60', border: 'border-border', icon: 'text-muted' },
}

// Map alert severity to colors
const severityColorMap = {
  info: 'primary',
  success: 'success',
  warning: 'warning',
  error: 'danger'
} as const

// Map alert type to icons (using only valid icons from the project)
const typeIconMap = {
  system: 'info',
  booking: 'calendar',
  payment: 'credit-card',
  availability: 'clock',
  promotion: 'star',
  security: 'shield',
  maintenance: 'settings',
  service: 'bell'
} as const

interface AlertDisplayProps {
  alert: Alert
  onDismiss?: (id: string) => void
  isStandalone?: boolean
  isDismissed?: boolean
}

export function AlertDisplay({ alert, onDismiss, isStandalone = false, isDismissed = false }: AlertDisplayProps) {
  const [isVisible, setIsVisible] = useState(true)

  // Handle dismissal with animation
  const handleDismiss = () => {
    setIsVisible(false)
    // Wait for animation to complete before calling onDismiss
    setTimeout(() => {
      if (onDismiss) onDismiss(alert.id)
    }, 300)
  }

  if (!isVisible) return null

  const severity = alert.severity as keyof typeof severityColorMap
  const type = alert.type as keyof typeof typeIconMap
  const color = isDismissed ? 'default' : severityColorMap[severity] || 'primary'
  const icon = typeIconMap[type] || 'bell'

  // For standalone alerts (displayed outside the dropdown)
  if (isStandalone) {
    const tone = ALERT_TONE[color] ?? ALERT_TONE.default
    return (
      <div
        className={cn(
          'relative flex items-start gap-2 p-3 border rounded-lg shadow-md transition-all duration-300 animate-in fade-in slide-in-from-top-5',
          tone.bg,
          tone.border,
        )}
        role='alert'
      >
        <div className={cn('shrink-0 mt-0.5', tone.icon)}>
          <Icon icon={icon} className='w-5 h-5' />
        </div>

        <div className='flex-1 min-w-0'>
          <div className='flex justify-between items-start'>
            <div className='text-sm font-medium text-foreground'>{alert.message}</div>

            {alert.dismissible && (
              <Button
                isIconOnly
                size='sm'
                variant='ghost'
                className='text-muted -mt-1 -mr-1'
                onPress={handleDismiss}
              >
                <Icon icon='x' className='w-4 h-4' />
              </Button>
            )}
          </div>

          <div className='mt-1 flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <Chip size='sm' color={color === 'primary' ? 'default' : color} variant='soft'>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </Chip>
              <span className='text-xs text-muted'>{new Date(alert.timestamp).toLocaleString()}</span>
            </div>

            {alert.link && (
              <Link
                href={alert.link.url}
                className={cn('text-sm hover:underline ml-auto', tone.icon)}
              >
                {alert.link.text}
              </Link>
            )}
          </div>
        </div>
      </div>
    )
  }

  // For card/popover alerts (HeroUI notification style)
  return (
    <div className='flex gap-3 px-6 py-4 bg-black/20 '>
      <div className='relative flex-none '>
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center ${
            color === 'primary'
              ? 'bg-info/15 '
              : color === 'success'
                ? 'bg-success/15'
                : color === 'warning'
                  ? 'bg-warning/15'
                  : color === 'danger'
                    ? 'bg-danger/15'
                    : 'bg-default/60'
          }`}
        >
          <Icon
            icon={icon}
            className={`w-5 h-5 ${
              color === 'primary'
                ? 'text-info'
                : color === 'success'
                  ? 'text-success'
                  : color === 'warning'
                    ? 'text-warning'
                    : color === 'danger'
                      ? 'text-danger'
                      : 'text-muted'
            }`}
          />
        </div>
        {!isDismissed && (
          <div className='absolute -top-1 -right-1 w-3 h-3 bg-accent rounded-full border-2 border-background'></div>
        )}
      </div>

      <div className='flex flex-col gap-1 flex-1 min-w-0'>
        <div className='flex justify-between items-start'>
          <p className='text-small text-foreground'>
            <strong className='font-medium'>{type.charAt(0).toUpperCase() + type.slice(1)}</strong> {alert.message}
          </p>
          {alert.dismissible && !isDismissed && (
            <Button
              isIconOnly
              size='sm'
              variant='ghost'
              className='text-muted -mt-1 -mr-1 min-w-6 w-6 h-6'
              onPress={handleDismiss}
            >
              <Icon icon='x' className='w-3 h-3' />
            </Button>
          )}
        </div>

        <div className='flex items-center justify-between'>
          <time className='text-tiny text-foreground'>
            <TimeDisplay utcDateTime={alert.timestamp} format='time' />
          </time>

          {alert.link && (
            <Link
              href={alert.link.url}
              className={`text-tiny hover:underline ${
                color === 'primary'
                  ? 'text-info'
                  : color === 'success'
                    ? 'text-success'
                    : color === 'warning'
                      ? 'text-warning'
                      : color === 'danger'
                        ? 'text-danger'
                        : 'text-muted'
              }`}
            >
              {alert.link.text}
            </Link>
          )}
        </div>

        {/* Optional: Add severity chip for better visual hierarchy */}
        <div className='flex items-center gap-2 mt-1'>
          <Chip size='sm' color={color === 'primary' ? 'default' : color} variant='soft' className='text-tiny'>
            {severity.charAt(0).toUpperCase() + severity.slice(1)}
          </Chip>
        </div>
      </div>
    </div>
  )
}
